import { useEffect, useRef, useState } from "react";
import { ManageOrdersViewModel } from "../../viewmodels/orders/ManageOrdersViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import type { PaginatorPageChangeEvent } from "primereact/paginator";

import * as actions from "../../store/actions/index";
import { formatDateWithTime, withRouter } from "../../shared/utility";

// Template Components
import DataManagementTemplate, { type DataColumn } from "../../components/templates/DataManagementTemplate";

// Other Components
import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import Pills, { PillType, type PillTypeValue } from "../../components/controls/Pills";
import OrdersCardsForMobile from "./OrdersCardsForMobile";
import type { AuthState } from "../../types/auth";
import type { CartState } from "../../types/orders";
import type { OrderListItem, OrderStatus } from "../../types/orders";
import { getSalesOrderNumber } from "../../shared/orderNumbers";

interface ManageOrdersProps { logout: () => void }
interface StoreState { auth: AuthState; cart: CartState }
const orderStatusContent: Record<OrderStatus, { text: string; type: PillTypeValue }> = {
  1: { text: "Created", type: PillType.Warning },
  3: { text: "Shipped", type: PillType.Success },
  8: { text: "Void", type: PillType.Error },
  9: { text: "Backordered", type: PillType.DarkYellow },
};

function ManageOrders(props: ManageOrdersProps) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    filterComponents,
    getCustomersForDropdown,
    getUsersForDropdown,
    getOrders,
    loading,
    orders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    askDeleteOrder,
    filters,
    resetFilters,
    setShowFilters,
    showFilters,
    openImportOrder,
  } = ManageOrdersViewModel(logout);

  const currentPage = useRef(1);
  useEffect(() => {
    getCustomersForDropdown();
    getOrders(null, currentPage.current, filters);
    getUsersForDropdown();
  }, []);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  // Search handler
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    getOrders(term || null, currentPage.current, filters);
  };

  // Pagination handler
  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    currentPage.current = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getOrders(searchTerm || null, currentPage.current, filters);
  };

  // Row click handler
  const handleRowClick = (order: OrderListItem) => {
    navigate(`/orders/${order.id}`);
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Create Order",
      onClick: () => navigate("/create-order"),
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
    {
      label: "Import Orders",
      onClick: () => openImportOrder(),
      className:
        "bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500",
    },
  ];

  // Column configurations for DataTable
  const columns: DataColumn<OrderListItem>[] = [
    {
      field: "company_name",
      header: "Customer",
      style: { width: "25%" },
      body: (rowData) => <span className="apollo-body">{rowData.company_name}</span>,
    },
    {
      field: "order_number",
      header: "Sales Order",
      style: { width: "15%" },
      body: (rowData) => (
        <span className="font-semibold text-primary-600 apollo-subheading">
          {getSalesOrderNumber(rowData)}
        </span>
      ),
    },
    {
      field: "created_at",
      header: "Date",
      style: { width: "20%" },
      body: (rowData) => (
        <span className="apollo-body">{formatDateWithTime(rowData.created_at)}</span>
      ),
    },
    {
      field: "total_dollar_amount",
      header: "Total",
      style: { width: "15%" },
      body: (rowData) => (
        <span className="font-semibold text-primary-700 apollo-subheading">
          ${rowData.total_dollar_amount}
        </span>
      ),
    },
    {
      field: "status",
      header: "Status",
      style: { width: "15%" },
      body: (rowData) => <Pills content={orderStatusContent[rowData.status]} />,
    },
    {
      header: "Actions",
      style: { width: "10%" },
      body: (rowData) => (
        <div className="flex gap-2">
          <Link to={`/orders/${rowData.id}`}>
            <button className="inline-flex items-center justify-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2">
              Edit
            </button>
          </Link>
          <button
            onClick={() => askDeleteOrder(rowData)}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Mobile cards component
  const OrdersCardsMobile = ({ data }: { data: OrderListItem[] }) => (
    <OrdersCardsForMobile askDeleteOrder={askDeleteOrder} orders={{ data: data }} />
  );

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: () => resetFilters(),
    onApply: () => {
      setShowFilters(false);
      getOrders(searchTerm || null, currentPage.current, filters);
    },
  };

  return (
    <div>
      <DataManagementTemplate
        title="Orders Management"
        description="Track and manage your orders efficiently"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        columns={columns}
        data={orders.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: orders.total || 0,
          template:
            "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} orders",
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by sales order, PO, invoice, or customer..."
        MobileCardsComponent={OrdersCardsMobile}
        emptyMessage="No orders found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={() => setEmptyModelOpen({ childComponent: null, open: false })}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (_state: StoreState) => {
  return {};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StoreState, unknown, AnyAction>) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ManageOrders));
